<template>
    <CodeDiff
        v-if="contentFlavor === ContentFlavor.TEXTUAL"
        :old-string="fromAsText"
        :new-string="toAsText"
        :filename="textByViewType[viewType].from"
        :newFilename="textByViewType[viewType].to"
        diffStyle="char"
        hideStat
        noDiffLineFeed
        :hideHeader="diffFormat === 'line-by-line'"
        :trim="trim"
        :output-format="diffFormat"
        :theme="ui.darkMode.value ? 'dark' : 'light'"
        v-bind="$attrs"
    />
    <ProcessDefinitionDiff
        v-else-if="mimetype === 'application/bpmn+xml'"
        :from="fromAsText"
        :to="toAsText"
        :fromName="textByViewType[viewType].from"
        :toName="textByViewType[viewType].to"
        :showAsDiff="Boolean(fromAsText)"
    />
    <NonTextualDiff
        v-else
        :from="from"
        :to="to"
        :fromName="textByViewType[viewType].from"
        :toName="textByViewType[viewType].to"
        :mimeType="mimetype"
        :diffFormat="diffFormat"
    />
</template>

<script lang="ts" setup>
import { CodeDiff } from "v-code-diff";
import { computed, ref, watch } from "vue";

import { ContentFlavor, getMimetypeFlavor } from "@/lib/contentFlavor";
import { useUIManager } from "@/stores/ui-store";

import NonTextualDiff from "./NonTextualDiff.vue";
import ProcessDefinitionDiff from "./ProcessDefinitionDiff.vue";

const ui = useUIManager();

type ViewType = "history" | "draft";

const props = withDefaults(
    defineProps<{
        from: Blob;
        to: Blob;
        viewType?: ViewType;
        trim?: boolean;
        mimetype?: string;
        diffFormat?: "side-by-side" | "line-by-line";
    }>(),
    {
        viewType: "history",
        name: "",
        newName: "",
        trim: true,
        mimetype: "",
        diffFormat: "side-by-side",
    }
);

const textByViewType: Record<ViewType, { to: string; from: string }> = {
    history: {
        from: "Previous version",
        to: "Selected version",
    },
    draft: {
        from: "Current version",
        to: "Your local draft",
    },
};

const contentFlavor = computed(() => {
    return getMimetypeFlavor(props.mimetype);
});

const fromAsText = ref("");
const toAsText = ref("");

// Only attempt to read as text for content types that should be displayed as text
watch(
    [() => props.from, () => props.to, contentFlavor],
    async () => {
        if (
            contentFlavor.value === ContentFlavor.TEXTUAL ||
            contentFlavor.value === ContentFlavor.DIAGRAM
        ) {
            try {
                fromAsText.value = await props.from.text();
            } catch (e) {
                fromAsText.value = "";
                console.error("Error reading 'from' blob as text:", e);
            }

            try {
                toAsText.value = await props.to.text();
            } catch (e) {
                toAsText.value = "";
                console.error("Error reading 'to' blob as text:", e);
            }
        }
    },
    { immediate: true }
);
</script>
